import moment from 'moment';
import PropTypes from 'prop-types';
import {useEffect, useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import ArrowWrapper, {SmallScreenArrowWrapper} from './arrow-wrapper';
import WindowSkeleton from './window-skeleton';

import {buildHandleEnterKeyPress} from '../utils/accessibility';
import DayPicker from './day-picker';
import Window from './window';
import ReactSelect from 'react-select';
import {usePageText} from '../context/page-text-context';
import {useTime} from '../context/time-context';
import {timeWindowSelected} from '../features/scheduling/schedulerSlice';
import {AlternateCalendarSVG} from './svgs';

const NoAvailabilityMessage = ({message}) => (
  <div className="no-times-container">
    <div className="no-times-message">{message}</div>
  </div>
);

const WindowSelectionLayout = ({
  arrowParams,
  dateSelectParams,
  maxDate,
  noSchedulableWindows,
  onCalendarToggle,
  preciseTimes,
  selectedDayWindows,
  validatingWindow,
  style,
  noTechniciansFoundMessage,
  noTimesAvailableMessage,
}) => {
  let windowContent;
  const dispatch = useDispatch();
  const {start} = useSelector((state) => state.scheduler);
  const {errors, fetchingWindows} = useTime();
  const pageText = usePageText();
  const {
    time: {timeStatus},
    errorMessages,
  } = pageText;
  const [errorsToDisplay, setErrorsToDisplay] = useState([]);
  const selectAWindow = ({start, end, utcOffset, appointmentId}) => {
    dispatch(timeWindowSelected({start: start.toISOString(), end: end.toISOString(), utcOffset, appointmentId}));
  };

  useEffect(() => {
    if (!fetchingWindows && errors?.length > 0) {
      const errorsObj = errors.map((str) => ({label: str, value: str}));
      setErrorsToDisplay(errorsObj);
    }
  }, [errors, fetchingWindows]);

  if (fetchingWindows) {
    windowContent = <WindowSkeleton />;
  } else if (!selectedDayWindows.length) {
    // assign to message the first value of errors array if it exists, otherwise assign NoTechniciansAvailable
    const message = errors ? errors[0] : noTechniciansFoundMessage;
    windowContent = <NoAvailabilityMessage message={message} />;
  } else if (maxDate && maxDate.isBefore(moment())) {
    windowContent = <NoAvailabilityMessage message={noTimesAvailableMessage} />;
  } else {
    windowContent = (
      <>
        <div className="status-keys-container">
          {[timeStatus.available, timeStatus.booked, timeStatus.selected].map((status, index) => (
            <div className="status-key-container" key={index}>
              <div className={`status-key custom-status-key-${index}`} />
              <span>{status}</span>
            </div>
          ))}
        </div>
        <div className="p-2 row-container grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 overflow-y-auto">
          {selectedDayWindows.map((window) => (
            <div className="window" key={window.start.toISOString()}>
              <Window
                isLoading={validatingWindow}
                onClick={selectAWindow}
                preciseTimes={preciseTimes}
                selectedWindowStart={moment(start)}
                timeZone={dateSelectParams.timeZone || window.timeZone}
                window={window}
              />
            </div>
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      {errorsToDisplay?.length > 0 && (
        <ReactSelect
          isSearchable={false}
          onChange={() => {}}
          options={errorsToDisplay}
          placeholder={errorMessages.errDetected}
          styles={errorContainerStyles}
        />
      )}

      <div className="appointment-container" style={style}>
        <div className="flex items-center justify-between p-4 bg-light-blue rounded-lg">
          <SmallScreenArrowWrapper {...arrowParams}>
            {fetchingWindows ? (
              <div />
            ) : (
              <h3 className="text-black font-bold">
                {dateSelectParams.selectedDay.isValid() ? moment(dateSelectParams.selectedDay).format('MMMM YYYY') : ''}
              </h3>
            )}

            <div
              onClick={onCalendarToggle}
              onKeyPress={buildHandleEnterKeyPress(onCalendarToggle)}
              style={{visibility: noSchedulableWindows ? 'hidden' : 'visible'}}
              tabIndex={0}>
              <AlternateCalendarSVG />
            </div>
          </SmallScreenArrowWrapper>
        </div>
        <hr className="border-light-gray" />
        <div className="p-2">
          <ArrowWrapper {...arrowParams}>
            <DayPicker {...dateSelectParams} />
          </ArrowWrapper>

          <div className="window-container">{windowContent}</div>
        </div>
      </div>
    </>
  );
};

export const errorContainerStyles = {
  container: (provided) => ({
    ...provided,
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    transform: 'scale(0.8)',
    margin: '1em',
    zIndex: 2,
  }),
  control: (provided) => ({
    ...provided,
    width: '90%',
  }),
  menu: (provided) => ({
    ...provided,
    width: '90%',
  }),
  option: (provided) => ({
    ...provided,
    color: 'white',
    backgroundColor: 'red',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: 'red',
    fontWeight: '400',
  }),
  singleValue: (provided) => ({...provided, color: 'red', fontWeight: '400'}),
};

WindowSelectionLayout.propTypes = {
  arrowParams: PropTypes.object.isRequired,
  dateSelectParams: PropTypes.object.isRequired,
  onCalendarToggle: PropTypes.func,
  selectedDayWindows: PropTypes.array,
  validatingWindow: PropTypes.bool,
};

WindowSelectionLayout.defaultProps = {
  onCalendarToggle: () => {},
  selectedDayWindows: [],
  validatingWindow: false,
};

export default WindowSelectionLayout;
